import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Lock, ChevronDown, ChevronUp, TrendingUp, Merge } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import BottomNav from "../components/BottomNav";

const StakePage = () => {
  const { user, refreshUser, authAxios } = useAuth();
  const { t } = useLanguage();
  const [stakes, setStakes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [stakeAmount, setStakeAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [liveEarnings, setLiveEarnings] = useState({});
  const [expandedView, setExpandedView] = useState(false);

  useEffect(() => { 
    if (user?.id) fetchStakes(); 
  }, [user?.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveEarnings(prev => {
        const updated = { ...prev };
        stakes.forEach(stake => {
          if (stake.is_active) {
            const rewardPerSecond = (stake.amount * 0.01) / 86400;
            updated[stake.id] = (prev[stake.id] || stake.earned) + rewardPerSecond;
          }
        });
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [stakes]);

  const fetchStakes = async () => {
    if (!user?.id) {
      console.log('No user id, skipping fetch');
      return;
    }
    console.log('Fetching stakes for user:', user.id);
    try {
      const api = authAxios();
      const res = await api.get(`/stake/list/${user.id}`);
      console.log('Stakes response:', res.data);
      setStakes(res.data || []);
      const earnings = {};
      (res.data || []).forEach(s => { earnings[s.id] = s.earned; });
      setLiveEarnings(earnings);
    } catch (e) { 
      console.error('Fetch stakes error:', e.response?.status, e.response?.data, e.message);
      setStakes([]);
    }
  };

  const createStake = async () => {
    if (!stakeAmount || parseFloat(stakeAmount) < 100) { toast.error("Minimum stake is 100 LTG"); return; }
    setLoading(true);
    try {
      const api = authAxios();
      await api.post(`/stake/create`, { amount: parseFloat(stakeAmount) });
      toast.success("Staked successfully!");
      setShowModal(false);
      setStakeAmount("");
      refreshUser();
      fetchStakes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const mergeStakes = async () => {
    if (stakes.length <= 1) {
      toast.error("You need at least 2 stakes to merge");
      return;
    }
    
    if (!window.confirm(`Merge all ${stakes.length} stakes into 1?\n\nTotal: ${totalStaked.toLocaleString()} LTG\nEarned: ${totalEarned.toFixed(2)} LTG\n\nNew unlock date will be 1 year from today.`)) {
      return;
    }
    
    setMerging(true);
    try {
      const api = authAxios();
      const res = await api.post(`/stakes/merge/${user.id}`);
      toast.success(res.data.message || "Stakes merged successfully!");
      refreshUser();
      fetchStakes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to merge stakes");
    } finally {
      setMerging(false);
    }
  };

  // Calculate totals
  const totalStaked = stakes.reduce((sum, s) => sum + s.amount, 0);
  const totalEarned = stakes.reduce((sum, s) => sum + (liveEarnings[s.id] || s.earned), 0);
  const totalUsdtValue = totalStaked / 1000;

  return (
    <div className="min-h-screen pb-24 p-4" data-testid="stake-page">
      <h1 className="font-manrope font-bold text-2xl">{t("stakeLTG")}</h1>
      <div className="flex flex-wrap gap-2 mt-2 mb-4">
        <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">1% Daily Reward</span>
        <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">{stakes.length} Stakes</span>
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-r from-yellow-500/10 to-green-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-muted-foreground text-xs">Total Staked</p>
            <p className="font-manrope font-bold text-xl text-primary">{totalStaked.toLocaleString()} LTG</p>
            <p className="text-yellow-400 text-sm">${totalUsdtValue.toFixed(2)} USDT</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Total Earned</p>
            <p className="font-manrope font-bold text-xl text-green-500">+{totalEarned.toFixed(2)} LTG</p>
            <p className="text-green-400 text-sm">${(totalEarned / 1000).toFixed(4)} USDT</p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-white/10">
          <TrendingUp size={14} className="text-green-500" />
          <span className="text-green-500 text-sm font-medium">+{(totalStaked * 0.01).toFixed(0)} LTG/day</span>
          <span className="text-muted-foreground text-sm">(${(totalStaked * 0.01 / 1000).toFixed(2)}/day)</span>
        </div>
      </div>

      <button onClick={() => setShowModal(true)} className="w-full btn-gold py-3 rounded-full text-lg mb-4" data-testid="new-stake-btn">
        + {t("stake")} LTG
      </button>

      {/* Merge Stakes Button - Only show if more than 1 stake */}
      {stakes.length > 1 && (
        <button 
          onClick={mergeStakes} 
          disabled={merging}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-3 rounded-full text-lg mb-4 flex items-center justify-center gap-2 transition-all"
          data-testid="merge-stakes-btn"
        >
          <Merge size={20} />
          {merging ? "Merging..." : `Merge All ${stakes.length} Stakes`}
        </button>
      )}

      {/* Stakes List */}
      {stakes.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {/* Header */}
          <div 
            className="flex items-center justify-between p-3 bg-black/30 cursor-pointer"
            onClick={() => setExpandedView(!expandedView)}
          >
            <span className="text-sm font-medium">All Stakes ({stakes.length})</span>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-green-500 text-xs">LIVE</span>
              {expandedView ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>

          {/* Scrollable Stakes List */}
          <div className={`overflow-y-auto transition-all duration-300 ${expandedView ? 'max-h-[60vh]' : 'max-h-[300px]'}`}>
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-black/20 text-xs text-muted-foreground sticky top-0">
              <div className="col-span-1">#</div>
              <div className="col-span-4">Staked</div>
              <div className="col-span-4">Earned</div>
              <div className="col-span-3">Unlock</div>
            </div>

            {/* Stakes Rows */}
            {stakes.map((stake, index) => (
              <div 
                key={stake.id} 
                className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-white/5 hover:bg-white/5 text-sm items-center"
              >
                <div className="col-span-1 text-muted-foreground text-xs">{index + 1}</div>
                <div className="col-span-4">
                  <p className="font-semibold text-primary">{stake.amount.toLocaleString()}</p>
                  <p className="text-yellow-500 text-xs">${(stake.amount / 1000).toFixed(2)}</p>
                </div>
                <div className="col-span-4">
                  <p className="font-semibold text-green-500 tabular-nums">
                    +{(liveEarnings[stake.id] || stake.earned).toFixed(4)}
                  </p>
                  <p className="text-green-400 text-xs">${((liveEarnings[stake.id] || stake.earned) / 1000).toFixed(6)}</p>
                </div>
                <div className="col-span-3 flex items-center gap-1">
                  <Lock size={10} className="text-yellow-500" />
                  <span className="text-xs text-muted-foreground">
                    {new Date(stake.unlock_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-2 bg-black/20 text-center">
            <p className="text-green-500/70 text-xs">Earnings auto-added to wallet • Withdraw at $5+</p>
          </div>
        </div>
      )}

      {stakes.length === 0 && (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <p className="text-muted-foreground">No stakes yet. Start earning 1% daily!</p>
        </div>
      )}

      {showModal && (
        <>
          <div className="modal-overlay" onClick={() => setShowModal(false)}></div>
          <div className="modal-content">
            <h2 className="font-manrope font-bold text-xl mb-4">New {t("stake")}</h2>
            <p className="text-green-500 text-center mb-2">Deposited LTG: {(user.sellable_ltg || 0).toFixed(2)}</p>
            <p className="text-muted-foreground text-xs text-center mb-4">(Only deposited/buy LTG can be stake)</p>
            <input type="number" placeholder="Amount (min 100 LTG)" value={stakeAmount} onChange={(e) => setStakeAmount(e.target.value)} className="input-dark mb-4" data-testid="stake-amount-input" />
            <button onClick={createStake} disabled={loading} className="w-full btn-gold py-3.5 rounded-full mb-8" data-testid="stake-submit-btn">
              {loading ? "..." : t("stake")} LTG
            </button>
          </div>
        </>
      )}

      <BottomNav />
    </div>
  );
};

export default StakePage;
